import { QueryClient } from '@tanstack/react-query';
import { ApiClientError } from './api.js';

/** Don't retry client errors (4xx); they won't get better. Retry transient/server errors twice. */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        if (error instanceof ApiClientError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return count < 2;
      },
    },
    mutations: { retry: false },
  },
});

/** Centralised query keys so invalidation stays consistent across hooks and the offline outbox. */
export const qk = {
  me: ['me'] as const,
  memberships: ['memberships'] as const,
  salon: ['salon'] as const,
  staff: ['staff'] as const,
  services: ['services'] as const,
  locations: ['locations'] as const,
  comms: ['comms'] as const,
  // WS8 comms send-pipeline — campaigns (paged, status-filtered), one campaign, the dry-run PREVIEW
  // (the bulk-spend safety surface — keyed on the campaign so it caches per-draft), the per-message
  // audit log (paged, whole-filter keyed), and the template list (kind/channel keyed). recipientCount /
  // estimatedCostPence / costPence are SERVER figures and comms reads are money-adjacent, so like the
  // billing/voucher keys these read live and are DELIBERATELY NOT added to PERSISTED_PREFIXES — a spend
  // estimate or send count is never served from an offline replica.
  campaigns: (status: string | null, offset: number) => ['campaigns', { status, offset }] as const,
  campaign: (id: string) => ['campaign', id] as const,
  campaignPreview: (id: string) => ['campaign', id, 'preview'] as const,
  commsMessages: (filter: Record<string, unknown>, offset: number) =>
    ['comms-messages', { ...filter, offset }] as const,
  commsTemplates: (kind: string | null, channel: string | null) =>
    ['comms-templates', { kind, channel }] as const,
  queue: ['queue'] as const,
  clients: (q: string, offset: number) => ['clients', { q, offset }] as const,
  client: (id: string) => ['client', id] as const,
  appointments: (date: string) => ['appointments', date] as const,
  workingHours: (staffId: string) => ['working-hours', staffId] as const,
  // WS7 scheduling depth — salon-wide calendar facts the diary + rota consume.
  closedDates: ['closed-dates'] as const,
  timeOff: (staffId: string) => ['time-off', staffId] as const,
  hourExceptions: (staffId: string) => ['hour-exceptions', staffId] as const,
  // WS9 pricing depth — variants, salon-wide levels, the matrix grid, and the resolved (shown==charged)
  // price. The resolve key carries the exact (service, variant?, staff?) tuple so each combination caches
  // independently; the matrix/variants key on the service so an editor edit invalidates precisely.
  serviceVariants: (serviceId: string) => ['service-variants', serviceId] as const,
  pricingLevels: ['pricing-levels'] as const,
  pricingMatrix: (serviceId: string) => ['pricing', 'matrix', serviceId] as const,
  resolvedPrice: (serviceId: string, variantId?: string, staffId?: string) =>
    ['pricing', 'resolve', serviceId, variantId ?? null, staffId ?? null] as const,
  // WS9 vouchers — the list (paged, status-filtered), one voucher + ledger, the till code lookup, and
  // the salon-wide liability report. Balances are server-derived and never persisted (read live).
  vouchers: (status: string | null, offset: number) => ['vouchers', { status, offset }] as const,
  voucher: (id: string) => ['voucher', id] as const,
  voucherLookup: (code: string) => ['vouchers', 'lookup', code] as const,
  voucherLiability: ['vouchers', 'liability-report'] as const,
  // WS9 discounts & promo codes — the audit trail (filterable), the reason report, promo CRUD list, and
  // the PUBLIC booking resolve (slug + code + base price → deterministic reduction).
  discounts: (query: Record<string, unknown>, offset: number) =>
    ['discounts', { ...query, offset }] as const,
  discountReport: (from: string | null, to: string | null) =>
    ['discounts', 'report', from, to] as const,
  promoCodes: (active: boolean | null, offset: number) =>
    ['promo-codes', { active, offset }] as const,
  promoResolve: (slug: string, code: string, serviceId: string | undefined, basePence: number) =>
    ['promo-resolve', slug, code, serviceId ?? null, basePence] as const,
  // WS9 commission & earnings + chair rent — config (rules/rents) and the server-computed reports.
  commissionRules: (staffId: string | null) => ['commission-rules', staffId] as const,
  chairRents: ['chair-rents'] as const,
  staffEarnings: (range: ReportRange) => ['staff-earnings', range] as const,
  chairRentReport: (range: ReportRange) => ['chair-rent-report', range] as const,
  // WS9 reports surface — the six server-computed reports (future-revenue projection, takings, comms cost,
  // occupancy, staff KPI, payment-cost composition). EVERY figure is a SERVER figure (projection/total/VAT/
  // net/fee): the hooks read live (staleTime 0) and these keys are DELIBERATELY NOT in PERSISTED_PREFIXES —
  // a projection or a takings total is never served from an offline replica. Each keys on its whole range so
  // a window change caches independently; takings also keys on groupBy.
  futureRevenue: (range: ReportRange, horizonWeeks: number) =>
    ['reports', 'future-revenue', range, horizonWeeks] as const,
  takings: (range: ReportRange, groupBy: string) => ['reports', 'takings', range, groupBy] as const,
  commsCost: (range: ReportRange) => ['reports', 'comms-cost', range] as const,
  occupancy: (range: ReportRange) => ['reports', 'occupancy', range] as const,
  staffKpi: (range: ReportRange) => ['reports', 'staff-kpi', range] as const,
  paymentCostComposition: (range: ReportRange) =>
    ['reports', 'payment-cost-composition', range] as const,
  // WS10 SaaS billing (Salonomy's own subscription) — the lifecycle/dunning state, the per-chair count
  // with the staff behind it, the itemised invoices + per-invoice breakdown, the comms-overage receipts,
  // and the webhook-event audit. EVERY one is a SERVER figure: these read live (staleTime 0 in the hooks)
  // and are NEVER added to PERSISTED_PREFIXES — money is never served from an offline replica.
  billingSubscription: ['billing', 'subscription'] as const,
  billingPlans: ['billing', 'plans'] as const,
  billingDunning: ['billing', 'dunning'] as const,
  billingInvoices: (status: string | null, offset: number) =>
    ['billing', 'invoices', { status, offset }] as const,
  billingInvoice: (id: string) => ['billing', 'invoice', id] as const,
  billingInvoiceBreakdown: (id: string) => ['billing', 'invoice', id, 'breakdown'] as const,
  billingOverage: (periodStart: string | null, periodEnd: string | null) =>
    ['billing', 'overage', periodStart, periodEnd] as const,
  billingEvents: (offset: number) => ['billing', 'events', offset] as const,
  // WS11 independent stylists — the practitioner (identity-spanning) layer. These reads are a READ-SIDE
  // UNION over the caller's own memberships (membershipsFor(userId) → per-leg withTenant() reads), each
  // row TAGGED by salon for the location label — never a cross-tenant join (ADR-0012 §2). The aggregated
  // reads (week/residencies/book) span identity, so like the billing keys they read live-ish (a
  // conservative staleTime in the hooks) and are NOT added to PERSISTED_PREFIXES — an identity-spanning
  // aggregate is never served from a single tenant's offline replica. `meResidencies` is the caller's
  // own memberships; the salon-side `salonResidencies` list (manager/owner) keys on the includeEnded flag.
  meResidencies: ['me', 'residencies'] as const,
  // WS10(b) — the practitioner's GLOBAL personal booking handle (book.salonomy.com/p/<handle>). An
  // identity-plane fact keyed on the HUMAN (never a tenant row), so like the other `me` aggregates it
  // reads live and is NOT in PERSISTED_PREFIXES — a public-handle claim is never served from a single
  // tenant's offline replica. 404 from the GET means UNCLAIMED (mapped to data:null, not an error).
  meBookingHandle: ['me', 'booking-handle'] as const,
  meWeek: (from: string, to: string) => ['me', 'week', from, to] as const,
  meBook: (search: string | null) => ['me', 'book', search] as const,
  meTimeOff: (from: string | null, to: string | null) => ['me', 'time-off', from, to] as const,
  salonResidencies: (includeEnded: boolean) => ['residencies', { includeEnded }] as const,
  // WS6 payments/receipts back-office — the takings LEDGER (paginated, filterable), one payment + its
  // splits, and the receipts list (now filterable by window/status/payment). MONEY HONESTY: every figure
  // is a server DTO figure; these reads get staleTime:0 in the hooks and are DELIBERATELY NOT added to
  // PERSISTED_PREFIXES — refundable/refunded/VAT money is never served from an offline replica. The list
  // keys carry the whole filter object so a window/status change caches independently.
  payments: (query: Record<string, unknown>) => ['payments', 'list', query] as const,
  payment: (id: string) => ['payment', id] as const,
  receipts: (query: Record<string, unknown>) => ['receipts', 'list', query] as const,
  // WS7 stock / products / suppliers — the product catalogue (paged + filterable), one product, the
  // supplier list, the per-active-product stock levels (onHand + signed valuation), and the append-only
  // movement ledger (paged + filterable). On-hand and stock valuation are SERVER-DERIVED (SUM of
  // qty_delta) and money-adjacent, so like the billing/voucher keys these read live and are DELIBERATELY
  // NOT added to PERSISTED_PREFIXES — a derived balance is never served from an offline replica. The
  // list keys carry the whole filter object so a filter change caches independently.
  products: (query: Record<string, unknown>, offset: number) =>
    ['products', { ...query, offset }] as const,
  product: (id: string) => ['product', id] as const,
  suppliers: ['suppliers'] as const,
  stockLevels: (kind: string | null, lowStockOnly: boolean | null) =>
    ['stock', 'levels', { kind, lowStockOnly }] as const,
  stockMovements: (filter: Record<string, unknown>, offset: number) =>
    ['stock', 'movements', { ...filter, offset }] as const,
  // WS13 reviews (LRU-05) — the operator moderation list (status-filtered; carries the salon aggregate)
  // and the per-salon settings (moderation mode + auto-request). A review renders on the PUBLIC booking
  // page + JSON-LD aggregateRating, so the averageRating / reviewCount are SERVER figures the console
  // only displays; the list reads live-ish (a moderation action must re-read the aggregate) and is NOT
  // added to PERSISTED_PREFIXES — a public-facing rating is never served from an offline replica. The
  // list key carries the status filter so each tab caches independently.
  reviews: (status: string | null) => ['reviews', { status }] as const,
  reviewSettings: ['reviews', 'settings'] as const,
  // WS-13 loyalty / points (LRU-06) — the per-salon program CONFIG (one row; inert defaults when unset)
  // and the per-member view (DERIVED balancePoints = signed sum of the ledger + history). Points are a
  // non-monetary perk, but a redeem converts points→pence and writes a `discount` line at the till, so a
  // member view is MONEY-ADJACENT: these reads get staleTime:0 in the hooks and are DELIBERATELY NOT
  // added to PERSISTED_PREFIXES — a derived balance (or a points→£ redemption) is never served from an
  // offline replica. The member key carries the clientId so each member caches independently.
  loyaltyProgram: ['loyalty', 'program'] as const,
  loyaltyMember: (clientId: string) => ['loyalty', 'member', clientId] as const,
  // WS-13 Feature 3a — prepaid packages / courses (LRU-14). A near-exact CLONE of the vouchers keys: the
  // catalogue list (active-filtered, paged), one purchased instrument + its ledger, the per-client
  // purchased-package list (status-filtered), and the salon-wide liability report. A package SALE is a
  // LIABILITY (never revenue); REMAINING sessions/value and OUTSTANDING liability are SERVER-DERIVED (the
  // signed sum of an append-only ledger, never a stored number). So like the voucher/billing keys these
  // reads are MONEY-ADJACENT — they get staleTime:0 in the hooks and are DELIBERATELY NOT added to
  // PERSISTED_PREFIXES (a derived balance / a liability total is never served from an offline replica).
  // The catalogue key carries the active filter + offset; the client-packages key the clientId + status,
  // so each combination caches independently and a sell/redeem/void invalidates precisely.
  packages: (active: boolean | null, offset: number) => ['packages', { active, offset }] as const,
  package: (id: string) => ['package', id] as const,
  clientPackages: (clientId: string | null, status: string | null) =>
    ['client-packages', { clientId, status }] as const,
  clientPackage: (id: string) => ['client-package', id] as const,
  packageLiability: ['packages', 'liability-report'] as const,
  // WS-13 Feature 3b — RECURRING MEMBERSHIPS (LRU-14, the other half of packages). A `£X/month`
  // subscription on the SALON's CONNECTED Stripe account, sold against a `membership_recurring` package.
  // The status / currentPeriodEnd / cancelAtPeriodEnd / benefitDiscountBps are a reconciled cache of the
  // connected-account subscription — SERVER figures the console only DISPLAYS (it never computes a charge,
  // a renewal, or a benefit reduction in JS). A SELL creates a subscription (synthetic/inert under the fake
  // gateway — no real charge on staging) and a CANCEL schedules end-of-period; both move a money-adjacent
  // figure, so like the billing/package/voucher keys these reads get staleTime:0 in the hooks and are
  // DELIBERATELY NOT added to PERSISTED_PREFIXES — a subscription state is never served from an offline
  // replica. The list key carries the clientId + status so each client/tab caches independently and a
  // sell/cancel invalidates precisely.
  clientMemberships: (clientId: string | null, status: string | null) =>
    ['client-memberships', { clientId, status }] as const,
  clientMembership: (id: string) => ['client-membership', id] as const,
  // WS-13 Feature 4 — CONSULTATION / CONSENT records (LRU-07). A PII/LIABILITY feature with NO money: a
  // record's snapshotTitle/snapshotBody is a FROZEN verbatim copy of the wording signed (reproducible
  // years later), records are append-only + immutable-once-signed, and templates are versioned-not-mutated.
  // Unlike the loyalty/voucher/billing keys these are NOT money-adjacent, so they read at the DEFAULT
  // staleTime (no staleTime:0 in the hooks). They are DELIBERATELY ABSENT from PERSISTED_PREFIXES too — but
  // for a PRIVACY reason, not a money one: sensitive consent PII (allergy/medical declarations, a signed
  // name) is never mirrored to an offline replica. The client-consents key carries the clientId and the
  // templates key the active filter, so each caches independently and a capture/version invalidates precisely.
  clientConsents: (clientId: string) => ['client-consents', clientId] as const,
  consentRecord: (id: string) => ['consent-record', id] as const,
  consentTemplates: (active: boolean | null) => ['consent-templates', { active }] as const,
  // WS-13 Feature 5 — BEFORE/AFTER PHOTOS (LRU-15). One key: the per-client photo metadata list (the
  // gallery groups it into before/after SETS by pairId client-side). The downloadUrls are SHORT-LIVED
  // server-minted signed GET URLs, so this is NOT cached aggressively (staleTime:0 in the hook — a stale
  // signed URL is a broken <img>, and a visibility/consent change must re-read). It is DELIBERATELY ABSENT
  // from PERSISTED_PREFIXES — but for PRIVACY, not money: identifiable client PII images (and their signed
  // capability URLs) are NEVER mirrored to an offline replica. Keyed on the clientId so each client's
  // gallery caches independently and any write (init/confirm/patch/consent/delete) invalidates precisely.
  clientPhotos: (clientId: string) => ['client-photos', clientId] as const,
  // WS-14 — NOTIFICATIONS INBOX. The salon-level ops inbox: a list (filterable by unread/kind, paged)
  // that ALSO carries the salon-wide unreadCount + total. read_at is SALON-WIDE shared read-state, so a
  // mark-read by anyone is a fact every session must re-read — the hooks invalidate the whole
  // ['notifications'] tree on any write. These are operational facts (not money), but they are live-ish
  // (a near-real-time inbox with a poll), so they are DELIBERATELY ABSENT from PERSISTED_PREFIXES — an
  // unread count is never served from an offline replica. The list key carries the whole filter so each
  // (unreadOnly, kind) view caches independently.
  notifications: (filter: { unreadOnly: boolean; kind: string | null }) =>
    ['notifications', filter] as const,
};

/** The date-range that keys a commission/earnings report (the report hooks pass their own range). */
interface ReportRange {
  from: string;
  to: string;
  staffId?: string;
  tz?: string;
}

/** Query-key prefixes whose data is mirrored to IndexedDB for offline open (read replica). */
export const PERSISTED_PREFIXES = [
  'salon',
  'staff',
  'services',
  'appointments',
  'clients',
  'queue',
];
